import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, SxProps } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import TextField from '../../../../components/TextField';
import colors from '../../../../constants/colors';
import {
  AmountFormValues,
  paymentAmountValidationSchema,
  validationMessages,
} from '../../validation';
import NumberFormatCustom from '../NumberFormatCustom';

interface PayAnotherAmountFieldProps {
  autoSubmit?: boolean;
  bill: BillingDetails;
  onSubmit: (values: AmountFormValues) => void;
  sx?: SxProps;
  tempAmount: string;
  handleChange?: (value: string) => void;
  testId?: string;
}

const PayAnotherAmountField = ({
  autoSubmit, // This is for the multi-policy payment page where another amount is added to the cart if it is valid
  bill,
  onSubmit,
  tempAmount,
  sx,
  handleChange,
  testId,
}: PayAnotherAmountFieldProps) => {
  const { control, handleSubmit, getValues, trigger } =
    useForm<AmountFormValues>({
      defaultValues: { amount: tempAmount },
      mode: 'onChange',
      resolver: yupResolver(
        paymentAmountValidationSchema(
          bill.totalDueAmount,
          bill.minimumDueAmount,
          bill.dueDate
        )
      ),
    });

  useEffect(() => {
    if (tempAmount) {
      trigger();
    }
  }, []);

  const autoSubmitProps = autoSubmit
    ? {
        disabled: false,
        onBlur: () => {
          const submitHandler = () => {
            onSubmit(getValues());
          };
          // submit if the validation fails or succeeds, we only need to show the message
          handleSubmit(submitHandler, submitHandler)();
        },
      }
    : {};

  const amountError = (errorMessage?: string) => {
    if (errorMessage === validationMessages.moreThanMin) return false;

    return !!errorMessage;
  };

  const lessThanMinMessage = (value: string) =>
    value !== bill.minimumDueAmount
      ? 'If you pay less than the minimum due, your account will be past due.'
      : '';

  return (
    <Box
      sx={{
        mt: 2,
        pr: { mobile: 0, tablet: 1 },
        width: { mobile: 1, tablet: 0.5 },
        ...sx,
      }}
    >
      <Controller
        name="amount"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            className="csaa-qm-encrypt"
            InputProps={{
              inputComponent:
                NumberFormatCustom as React.ComponentType<unknown>,
              sx: { backgroundColor: colors.csaaWhite },
            }}
            helperText={error?.message ?? lessThanMinMessage(value)}
            fullWidth
            label="Amount"
            inputProps={{
              'data-testid': testId ?? 'pay-another-amount-field',
              maxLength: 50,
            }}
            {...autoSubmitProps}
            onChange={(e) => {
              handleChange && handleChange(e.target.value);
              onChange(e);
            }}
            value={value}
            error={amountError(error?.message)}
          />
        )}
      />
    </Box>
  );
};

export default PayAnotherAmountField;
